import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import TaxInvoiceView from "../components/TaxInvoiceView";
import { downloadPdfBlob, generateInvoicePdfBlob } from "../lib/invoicePdf";
import { publicApi } from "../lib/publicApi";

export default function PublicInvoice() {
  const { token } = useParams();
  const invoiceRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPdf, setSavingPdf] = useState(false);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/public/invoices/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const savePdf = async () => {
    if (!invoiceRef.current || !data?.sale) return;
    setSavingPdf(true);
    try {
      const filename = `${String(data.sale.invoice_no).replace(/[^\w.-]+/g, "_")}.pdf`;
      const blob = await generateInvoicePdfBlob(invoiceRef.current, filename);
      downloadPdfBlob(blob, filename);
    } catch {
      toast.error("Could not save PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white text-sm text-gray-500">
        Loading invoice…
      </div>
    );
  }

  if (!data?.sale) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6 text-center">
        <p className="text-lg font-semibold text-gray-800">Invoice not found</p>
        <p className="mt-2 text-sm text-gray-500">This link may be invalid or expired.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4" data-testid="public-invoice-root">
      <div className="max-w-5xl mx-auto">
        <div className="no-print flex flex-wrap items-center justify-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => window.print()}
            className="h-10 px-4 rounded-md border font-semibold inline-flex items-center gap-2 bg-white"
            style={{ borderColor: "var(--border-soft)" }}
          >
            <Printer size={16} /> Print
          </button>
          <button
            type="button"
            onClick={savePdf}
            disabled={savingPdf}
            className="h-10 px-4 rounded-md text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            style={{ background: "var(--primary)" }}
          >
            <Download size={16} /> {savingPdf ? "Saving…" : "Save as PDF"}
          </button>
        </div>

        <TaxInvoiceView ref={invoiceRef} sale={data.sale} store={data.store} />
      </div>
    </div>
  );
}
